import React from 'react';
import { Building2, Plus, Search } from 'lucide-react';
import type { VendorCapabilityFilter, VendorOrgDirectoryRow } from '../../../utils/vendorOrgSummary';
import { capabilityLabel } from '../../../utils/vendorHelpers';

export interface VendorDirectoryProps {
  rows: VendorOrgDirectoryRow[];
  selectedOrgId: string | null;
  searchQuery: string;
  capabilityFilter: VendorCapabilityFilter;
  showCreateForm: boolean;
  createName: string;
  createBusy: boolean;
  onSearchChange: (q: string) => void;
  onFilterChange: (f: VendorCapabilityFilter) => void;
  onSelectOrg: (orgId: string) => void;
  onToggleCreate: () => void;
  onCreateNameChange: (name: string) => void;
  onCreateSubmit: () => void;
}

const FILTERS: { id: VendorCapabilityFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'timber', label: 'Timber' },
  { id: 'cnc', label: 'CNC' },
  { id: 'both', label: 'Both' },
];

const VendorDirectory: React.FC<VendorDirectoryProps> = ({
  rows,
  selectedOrgId,
  searchQuery,
  capabilityFilter,
  showCreateForm,
  createName,
  createBusy,
  onSearchChange,
  onFilterChange,
  onSelectOrg,
  onToggleCreate,
  onCreateNameChange,
  onCreateSubmit,
}) => (
  <aside className="ops-sidebar vm-sidebar">
    <div className="ops-sidebar-header">
      <span className="ops-sidebar-label">Vendors</span>
    </div>

    <div className="vm-sidebar-controls">
      <div className="vm-sidebar-search">
        <Search size={13} className="vm-sidebar-search-icon" aria-hidden />
        <input
          type="search"
          className="vm-sidebar-search-input"
          placeholder="Search name or email…"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="ops-hub-views vm-sidebar-views" role="group" aria-label="Filter by capability">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`ops-view-btn ${capabilityFilter === f.id ? 'active' : ''}`}
            onClick={() => onFilterChange(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>

    {showCreateForm && (
      <div className="vm-sidebar-create">
        <input
          type="text"
          className="ops-modal-input"
          placeholder="Company name"
          value={createName}
          autoFocus
          onChange={(e) => onCreateNameChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void onCreateSubmit()}
        />
        <div className="vm-sidebar-create-actions">
          <button
            type="button"
            className="ops-hide-completed-btn"
            onClick={onToggleCreate}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ops-new-btn vm-sidebar-create-submit"
            disabled={createBusy || !createName.trim()}
            onClick={() => void onCreateSubmit()}
          >
            {createBusy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    )}

    <nav className="ops-sidebar-nav vm-sidebar-nav">
      {rows.length === 0 ? (
        <p className="vm-sidebar-empty">No vendors match your search.</p>
      ) : (
        rows.map((row) => (
          <button
            key={row.orgId}
            type="button"
            className={`ops-sidebar-item vm-sidebar-item ${selectedOrgId === row.orgId ? 'active' : ''}`}
            onClick={() => onSelectOrg(row.orgId)}
          >
            <span className="ops-sidebar-item-icon">
              <Building2 size={15} aria-hidden />
            </span>
            <span className="ops-sidebar-item-label">
              <span className="vm-sidebar-item-name">{row.org.name}</span>
              <span className="vm-sidebar-item-meta">
                {row.capabilities.map((c) => capabilityLabel(c)).join(' · ')}
                {' · '}
                {row.inviteCount} user{row.inviteCount === 1 ? '' : 's'}
              </span>
            </span>
            {row.activeProjectCount > 0 && (
              <span className="ops-sidebar-item-count">{row.activeProjectCount}</span>
            )}
          </button>
        ))
      )}
    </nav>

    <div className="ops-sidebar-footer">
      <button type="button" className="ops-sidebar-new-btn" onClick={onToggleCreate}>
        <Plus size={14} aria-hidden />
        Add vendor
      </button>
    </div>
  </aside>
);

export default VendorDirectory;
